import { ApiUrl } from '@config/apiUrl';
import apiProtected from '@lib/api/apiClient';
import { useSession } from '@lib/auth/session';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type ClientTokenResponse,
  type PaymentMethodsResponse,
  readClientToken,
  type SavedCard,
} from './paymentTypes';

const server = ApiUrl.server_url;

const paymentQueryKeys = {
  methods: ['payments', 'methods'] as const,
  clientToken: ['payments', 'client-token'] as const,
};

export const usePaymentMethodsQuery = () => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: paymentQueryKeys.methods,
    queryFn: async (): Promise<SavedCard[]> => {
      const { data } = await apiProtected.get<PaymentMethodsResponse>(
        `${server}v1/braintree/get-all-payment-methods`
      );
      return data.data ?? [];
    },
    enabled: loggedIn,
  });
};

export const useClientTokenQuery = () => {
  const loggedIn = useSession();
  return useQuery({
    queryKey: paymentQueryKeys.clientToken,
    queryFn: async (): Promise<string | null> => {
      const { data } = await apiProtected.get<ClientTokenResponse>(
        `${server}v1/braintree/get-client-token`
      );
      return readClientToken(data);
    },
    enabled: loggedIn,
  });
};

const usePaymentMutation = <TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: paymentQueryKeys.methods,
      });
    },
  });
};

export const useAddPaymentMethodMutation = () => {
  return usePaymentMutation((nonce: string) =>
    apiProtected.post(`${server}v1/braintree/add-payment-method`, { nonce })
  );
};

export const useMarkCardDefaultMutation = () => {
  return usePaymentMutation((token: string) =>
    apiProtected.patch(`${server}v1/braintree/mark-card-as-default`, { token })
  );
};

export const useRemoveCardMutation = () => {
  return usePaymentMutation((token: string) =>
    apiProtected.delete(`${server}v1/braintree/remove-card`, {
      data: { token },
    })
  );
};
